/**
 * MonitoredProductsTable Component
 *
 * Displays tracked products in an IndexTable with status badges.
 * Clicking a product opens the settings modal for threshold configuration.
 */

import {
  IndexTable,
  Text,
  InlineStack,
  Box,
  Icon,
} from '@shopify/polaris';
import { ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import type { TrackedProduct, OnboardingData } from '../../types/dashboard';
import { ProductCell } from './ProductCell';
import { DataPill } from '../ui/DataPill';
import { getStatusBadge, getProductStatus } from './badge-utils';
import { isTestProduct } from '~/utils/test-data-helpers';

/**
 * Props for MonitoredProductsTable component
 */
export interface MonitoredProductsTableProps {
  products: TrackedProduct[];
  onboardingData: OnboardingData | null;
  shopDomain: string;
  /** Called when a product row is clicked - opens settings modal */
  onProductClick: (product: TrackedProduct) => void;
  /** Whether the shop has test data (used for test badge display) */
  hasTestData?: boolean;
  // Pagination
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

/**
 * Get status badge for a product based on alert severities
 */
function getProductStatusBadge(product: TrackedProduct) {
  const hasData = product.variant_count > 0 || product.total_inventory > 0;
  const status = getProductStatus(product.alert_severities, hasData);
  return getStatusBadge(status);
}

/**
 * Monitored Products IndexTable component
 */
export function MonitoredProductsTable({
  products,
  onboardingData,
  shopDomain,
  onProductClick,
  hasTestData = false,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
}: MonitoredProductsTableProps) {
  // Calculate paginated products
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = products.slice(startIndex, endIndex);

  return (
    <>
      <div className="animated-list">
        <IndexTable
          resourceName={{ singular: 'product', plural: 'products' }}
          itemCount={paginatedProducts.length}
          headings={[
            { title: 'Product' },
            { title: 'Variants', alignment: 'end' },
            { title: 'Stock', alignment: 'end' },
            { title: 'Thresholds', alignment: 'center' },
            { title: 'Status', alignment: 'center' },
          ]}
          selectable={false}
        >
          {paginatedProducts.map((product, index) => (
            <IndexTable.Row
              id={product.product_id}
              key={product.product_id}
              position={index}
              onClick={() => {
                if (shopDomain) {
                  onProductClick(product);
                }
              }}
            >
              <IndexTable.Cell>
                <ProductCell
                  title={product.product_title}
                  imageUrl={product.image_url}
                  showTestBadge={hasTestData && isTestProduct(product.product_title)}
                />
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="end">
                  <DataPill value={product.variant_count || 0} label="variants" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="end">
                  <DataPill value={product.total_inventory || 0} label="units" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  <Text as="span" variant="bodyMd">
                    {product.urgent_threshold_days}d / {product.warning_threshold_days}d
                  </Text>
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  {getProductStatusBadge(product)}
                </InlineStack>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      </div>

      {/* Pagination - using native buttons for reliable click handling in embedded apps */}
      {products.length > itemsPerPage && (
        <Box paddingBlockStart="400">
          <InlineStack align="center" gap="200" blockAlign="center">
            <button
              type="button"
              className="pagination-button"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <Icon source={ChevronLeftIcon} />
            </button>
            <Text as="span" variant="bodyMd">
              Page {currentPage} of {totalPages}
            </Text>
            <button
              type="button"
              className="pagination-button"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <Icon source={ChevronRightIcon} />
            </button>
          </InlineStack>
          <Box paddingBlockStart="200">
            <InlineStack align="center">
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Showing {startIndex + 1}-{Math.min(endIndex, products.length)} of {products.length} products
              </Text>
            </InlineStack>
          </Box>
        </Box>
      )}
    </>
  );
}
